import React, {useState} from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {useTranslate} from '@computerrock/formation-i18n';
import {useRouter} from '@computerrock/formation-router';
import {Icon, Pill, useStyles, eyeIcon, Info, DisplayCurrency} from '@ace-de/ui-components';
import {mafInvoiceSubmissionStatuses} from '@ace-de/eua-entity-types';
import {invoiceSubmissionStatusesIcons} from '../constants/invoiceSubmisionStatuses';
import modalIds from '../../modalIds';

const InvoiceSubmission = props => {
    const {cx} = useStyles();
    const {createTranslateShorthand, translate, activeLocale} = useTranslate();
    const translateTab = createTranslateShorthand('invoice_submission');
    const {openModal} = useRouter();
    const {invoiceSubmissionId, paidAmount, status, statusDescription} = props;
    const {creationDate, invoiceSubmission} = props;
    const [isSummaryViewActive, setIsSummaryViewActive] = useState(false);
    const totalRequestedInvoicingAmount = invoiceSubmission.services && invoiceSubmission.services.filter(service => (
        !!service.requestedInvoicingAmount
    )).map(service => (Number(service.requestedInvoicingAmount)))
        .reduce((accumulator, currentValue) => (accumulator + currentValue), 0);

    const toggleView = () => {
        setIsSummaryViewActive(!isSummaryViewActive);
        openModal(modalIds.INVOICE_SUMMARY_VIEW_MODAL, {invoiceSubmissionId});
    };

    const statusTypes = {
        [mafInvoiceSubmissionStatuses.CREDITING_REQUESTED]: 'negative',
        [mafInvoiceSubmissionStatuses.PROCESSING]: 'information-highlighted',
        [mafInvoiceSubmissionStatuses.PARTIAL_REFUND]: 'positive',
        [mafInvoiceSubmissionStatuses.TOTAL_REFUND]: 'positive',
        [mafInvoiceSubmissionStatuses.DENIED]: 'negative',
    };

    return (
        <div
            className={cx([
                'global!ace-u-flex',
                'global!ace-u-flex--direction-column',
            ])}
        >
            <div
                className={cx([
                    'global!ace-u-width--full',
                    'global!ace-u-flex',
                    'global!ace-u-flex--align-center',
                    'global!ace-u-flex--small-justify-space-between',
                ])}
            >
                <p
                    className={cx([
                        'global!ace-u-typography--variant-body-bold',
                        'global!ace-u-margin--small-bottom-4',
                        'global!ace-u-flex--large-basis-45',
                    ])}
                >
                    {invoiceSubmission.services?.length > 1
                        ? translateTab('invoice_label.multiple_services')
                        : invoiceSubmission.services?.length > 0
                            ? translate(`global.service_type.${invoiceSubmission.services[0].type.toLowerCase()}`)
                            : ''}
                </p>
                <Icon
                    icon={eyeIcon}
                    className={cx(['ace-c-icon--xl', 'ace-c-icon--color-highlight-light'])}
                    onClick={toggleView}
                />
            </div>
            <p
                className={cx([
                    'global!ace-u-typography--variant-caption',
                    'global!ace-u-typography--color-medium-emphasis',
                    'global!ace-u-margin--small-bottom-4',
                ])}
            >
                {translateTab('text.case_number', {invoiceSubmissionId})}
            </p>
            <p
                className={cx([
                    'global!ace-u-typography--variant-caption',
                    'global!ace-u-typography--color-medium-emphasis',
                    'global!ace-u-margin--small-bottom-4',
                ])}
            >
                {`${invoiceSubmission.services?.length > 1
                    ? invoiceSubmission.services
                        .map(service => translate(`global.service_type.${service.type.toLowerCase()}`))
                        .join(', ')
                    : invoiceSubmission.services?.length > 0
                        ? translate(`global.service_type.${invoiceSubmission.services[0].type.toLowerCase()}`)
                        : ''}, ${translateTab('services_description.submission_date', {date: creationDate
                    ? moment(creationDate, moment.ISO_8601).format('DD.MM.YYYY')
                    : ''})}`}
            </p>
            <div
                className={cx([
                    'global!ace-u-flex',
                    'global!ace-u-flex--justify-space-between',
                    'global!ace-u-margin--small-bottom-4',
                    'global!ace-u-margin--small-top-8',
                ])}
            >
                <p className={cx('global!ace-u-typography--variant-caption')}>
                    {translateTab('text.amount_requested')}
                </p>
                <p className={cx('global!ace-u-typography--variant-caption')}>
                    <DisplayCurrency
                        price={totalRequestedInvoicingAmount}
                        activeLocale={activeLocale}
                    />
                </p>
            </div>
            {(status === mafInvoiceSubmissionStatuses.PARTIAL_REFUND
                || status === mafInvoiceSubmissionStatuses.TOTAL_REFUND
                || status === mafInvoiceSubmissionStatuses.FINISHED) && (
                    <div
                        className={cx([
                            'global!ace-u-flex',
                            'global!ace-u-flex--justify-space-between',
                            'global!ace-u-margin--small-bottom-4',
                        ])}
                    >
                        <p className={cx('global!ace-u-typography--variant-caption')}>
                            {translateTab('text.amount_granted')}
                        </p>
                        <p className={cx('global!ace-u-typography--variant-caption')}>
                            <DisplayCurrency
                                price={paidAmount}
                                activeLocale={activeLocale}
                            />
                        </p>
                    </div>
            )
            }
            <Pill
                className={cx([
                    'global!ace-u-margin--small-bottom-8',
                    'global!ace-u-margin--small-top-8',
                    'global!ace-u-width--max-content',
                ])}
                type={statusTypes[status]}
            >
                <div className={cx(['global!ace-u-flex', 'global!ace-u-flex--align-center'])}>
                    {invoiceSubmissionStatusesIcons[status] && (
                        <Icon
                            icon={invoiceSubmissionStatusesIcons[status]}
                            className={cx('ace-c-icon--m', {
                                'ace-c-icon--color-warning': status === mafInvoiceSubmissionStatuses.CREDITING_REQUESTED
                                || status === mafInvoiceSubmissionStatuses.DENIED,
                                'ace-c-icon--color-success': status === mafInvoiceSubmissionStatuses.PARTIAL_REFUND
                                || status === mafInvoiceSubmissionStatuses.TOTAL_REFUND,
                                'ace-c-icon--color-information': status === mafInvoiceSubmissionStatuses.PROCESSING,
                            })}
                        />
                    )}
                    <span>{translate(`global.invoice_submission_status.${status.toLowerCase()}`)}</span>
                </div>
            </Pill>
            {[mafInvoiceSubmissionStatuses.PARTIAL_REFUND,
                mafInvoiceSubmissionStatuses.DENIED,
                mafInvoiceSubmissionStatuses.FINISHED].includes(status)
                     && (
                     <Info className={cx('global!ace-u-margin--small-bottom-8')}>
                         {statusDescription}
                     </Info>
                     )}
        </div>
    );
};

InvoiceSubmission.propTypes = {
    invoiceSubmissionId: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    statusDescription: PropTypes.string.isRequired,
    creationDate: PropTypes.string.isRequired,
    paidAmount: PropTypes.string,
    invoiceSubmission: PropTypes.object,
};

InvoiceSubmission.defaultProps = {
    paidAmount: PropTypes.number,
    invoiceSubmission: {},
};

export default InvoiceSubmission;
